import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { VisibilityState } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { UserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { IUsersService } from "services/UsersService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

const initialState = {
  Users: new Array<UserModel>(),
  IsLoading: false,
  IncludeInactive: false,
  SavedColumnOrder: Array<string>(),
  VisibleColumns: {},
};

export const fetchUserTableVisibleColumns = createAsyncThunk("UsersTable/FetchUserTableVisibleColumns", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getVisibleColumns(SETTING_TYPES.UserTableVisibleColumns);
});

export const updateUserTableVisibleColumns = createAsyncThunk(
  "UsersTable/UpdateUserTableVisibleColumns",
  async (tableVisibleColumns: VisibilityState) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setVisibleColumns(tableVisibleColumns, SETTING_TYPES.UserTableVisibleColumns);
  }
);

export const fetchUserTableColumnOrder = createAsyncThunk("UsersTable/FetchUserTableColumnOrder", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getColumnOrder(SETTING_TYPES.UserTableColumnKey);
});

export const updateUserTableColumnOrder = createAsyncThunk(
  "UsersTable/UpdateUserTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.UserTableColumnKey);
  }
);

export const fetchAllUsers = createAsyncThunk("UsersTable/FetchAllUsers", async (includeInactive: boolean) => {
  const usersService = container.get<IUsersService>(SERVICE_TYPES.UsersService);
  return await usersService.getAllUsers(includeInactive);
});

export const updateUserActiveStatus = createAsyncThunk<void, { userID: string }, { state: RootState }>(
  "UsersTable/UpdateUserActiveStatus",
  async ({ userID }, thunkAPI) => {
    const usersService = container.get<IUsersService>(SERVICE_TYPES.UsersService);
    await usersService.updateUserActiveStatus(userID);
    const state = thunkAPI.getState();
    thunkAPI.dispatch(fetchAllUsers(state.userTable.IncludeInactive));
  }
);

export const userTableSlice = createSlice({
  name: "usersTable",
  initialState,
  reducers: {
    setIncludeInactive: (state, action) => {
      state.IncludeInactive = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.IsLoading = true;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.IsLoading = false;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.IsLoading = false;
        state.Users = action.payload;
      })
      .addCase(updateUserActiveStatus.fulfilled, () => {
        toast.success("Profile Status Updated");
      })
      .addCase(fetchUserTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateUserTableColumnOrder.fulfilled, () => {
        toast.success("Column order saved");
      })
      .addCase(fetchUserTableVisibleColumns.fulfilled, (state, action) => {
        state.VisibleColumns = action.payload;
      });
  },
});

export const { setIncludeInactive } = userTableSlice.actions;

export const selectAllUsers = (state: RootState) => state.userTable.Users;
export const selectIncludeInactive = (state: RootState) => state.userTable.IncludeInactive;
export const selectIsLoading = (state: RootState) => state.userTable.IsLoading;
export const selectSavedColumnOrder = (state: RootState) => state.userTable.SavedColumnOrder;
export const selectVisibleColumns = (state: RootState) => state.userTable.VisibleColumns;

export default userTableSlice.reducer;
